import { useEffect, useState } from 'react';
import { ScreenFrame } from '@/components/Screen';
import { MetricCard, MetricGrid, SectionCard, TabsRow } from '@/components/DataList';
import { Icon } from '@/components/icons';
import { ImagePicker, mediaUrl } from '@/components/ImagePicker';
import { admin } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Empty,
  ErrorBox,
  FlashMessage,
  Form,
  FormActions,
  Input,
  Loading,
  Select,
  Textarea,
  VisibilitySelect,
} from '@/components/ui';
import { str } from '@/utils/format';

type MaintenanceForm = {
  maintenance_mode: boolean;
  maintenance_title: string;
  maintenance_message: string;
};

function placementLabel(p: unknown): string {
  const key = str(p, '');
  if (key === 'units') return 'صفحة الوحدات';
  if (key === 'games') return 'صفحة الألعاب';
  if (key === 'all') return 'الصفحتان';
  return key || '—';
}

const emptySlide = {
  id: 0,
  placement: 'units',
  title: 'سلايد',
  subtitle: '',
  image_url: '',
  link_screen: '',
  is_active: 1,
  sort_order: 0,
};

export function SiteSettingsScreen() {
  const state = useAsync(
    () => admin.siteSettings(),
    (p) => (p.data && typeof p.data === 'object' ? (p.data as JsonMap) : (p as JsonMap)),
  );
  const [form, setForm] = useState<MaintenanceForm>({
    maintenance_mode: false,
    maintenance_title: 'الموقع قيد الصيانة',
    maintenance_message: 'نقوم حالياً بتحديث الخدمة. الرجاء المحاولة لاحقاً.',
  });
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!state.data) return;
    setForm({
      maintenance_mode: Boolean(state.data.maintenance_mode),
      maintenance_title: str(state.data.maintenance_title, form.maintenance_title),
      maintenance_message: str(state.data.maintenance_message, form.maintenance_message),
    });
  }, [state.data]);

  async function save() {
    setBusy(true);
    const res = await admin.saveSiteSettings(form);
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تم حفظ الإعدادات.') : errorMessage(res));
    if (res.success) state.reload();
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="settings"
      title="إعدادات الموقع"
      subtitle="فعّل وضع الصيانة لإيقاف واجهة المستخدم مؤقتاً مع إبقاء لوحة الإدارة متاحة."
      actions={
        <Button type="button" variant="secondary" onClick={() => state.reload()}>
          <Icon name="refresh" size={18} /> تحديث
        </Button>
      }
    >
      <FlashMessage message={msg} />

      <section className="pro-grid two">
        <SectionCard
          title="وضع الصيانة"
          subtitle="فعّل هذا الخيار أثناء التحديثات أو نقل الخادم لإظهار صفحة صيانة واضحة للمستخدمين، مع إبقاء لوحة الإدارة متاحة."
          icon={<Icon name="settings" size={24} />}
        >
          <Form className="form-grid two-columns" onSubmit={() => void save()}>
            <Select
              label="الحالة"
              value={form.maintenance_mode ? '1' : '0'}
              onChange={(v) => setForm((f) => ({ ...f, maintenance_mode: v === '1' }))}
              options={[
                { value: '0', label: 'الموقع يعمل' },
                { value: '1', label: 'قيد الصيانة' },
              ]}
            />
            <Input
              label="عنوان الصفحة"
              value={form.maintenance_title}
              onChange={(e) => setForm((f) => ({ ...f, maintenance_title: e.target.value }))}
            />
            <Textarea
              className="span-2"
              label="رسالة الصيانة"
              value={form.maintenance_message}
              onChange={(v) => setForm((f) => ({ ...f, maintenance_message: v }))}
              rows={4}
            />
            <FormActions>
              <Button type="submit" disabled={busy}>
                {busy ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
              </Button>
            </FormActions>
          </Form>
        </SectionCard>

        <SectionCard
          title="معاينة صفحة المستخدم"
          subtitle="معاينة صفحة الصيانة عند تفعيل الوضع."
          icon={<Icon name="bell" size={24} />}
        >
          <div className="maintenance-preview">
            <strong>{form.maintenance_title || 'الموقع قيد الصيانة'}</strong>
            <p>{form.maintenance_message || 'نقوم حالياً بتحديث الخدمة. الرجاء المحاولة لاحقاً.'}</p>
            <span className={`pro-badge ${form.maintenance_mode ? 'off' : 'ok'}`}>
              {form.maintenance_mode ? 'الصيانة مفعلة' : 'الموقع متاح'}
            </span>
          </div>
        </SectionCard>
      </section>
    </ScreenFrame>
  );
}

export function SlidesScreen() {
  const state = useAsync(
    () => admin.slides(),
    (p) => pickList(p, 'data', 'slides', 'items'),
  );
  const [form, setForm] = useState(emptySlide);
  const [filter, setFilter] = useState<'all' | 'units' | 'games'>('all');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const list = state.data ?? [];

  const filtered = list.filter((s) => filter === 'all' || str(s.placement) === filter);

  async function save() {
    if (!form.image_url.trim()) {
      setMsg('صورة السلايد مطلوبة.');
      return;
    }
    setBusy(true);
    const res = await admin.saveSlide(form);
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تم حفظ السلايد.') : errorMessage(res));
    if (res.success) {
      setForm(emptySlide);
      state.reload();
    }
  }

  async function remove(id: unknown) {
    if (!window.confirm('حذف السلايد؟')) return;
    const res = await admin.deleteSlide({ id });
    if (!res.success) {
      window.alert(errorMessage(res));
      return;
    }
    state.reload();
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="package"
      title="سلايدر الواجهة"
      subtitle="ارفع صور السلايدر وحدد مكان ظهورها وترتيبها."
      actions={
        <Button type="button" onClick={() => setForm(emptySlide)}>
          <Icon name="package" size={18} /> سلايد جديد
        </Button>
      }
    >
      <MetricGrid>
        <MetricCard title="كل السلايدات" value={String(list.length)} icon={<Icon name="package" size={21} />} />
        <MetricCard
          title="سلايدر الوحدات"
          value={String(list.filter((s) => str(s.placement) === 'units').length)}
          icon={<Icon name="wallet" size={21} />}
        />
        <MetricCard
          title="سلايدر الألعاب"
          value={String(list.filter((s) => str(s.placement) === 'games').length)}
          icon={<Icon name="game" size={21} />}
        />
        <MetricCard
          title="الظاهر"
          value={String(list.filter((s) => Number(s.is_active) !== 0).length)}
          icon={<Icon name="check" size={21} />}
        />
      </MetricGrid>

      <FlashMessage message={msg} tone={msg.includes('مطلوب') ? 'error' : 'ok'} />

      <SectionCard
        title={form.id ? 'تعديل سلايد' : 'إضافة سلايد'}
        subtitle="المقاس الموصى به 1500×500، وتُعرض الصورة دون نصوص إضافية."
        icon={<Icon name="package" size={24} />}
      >
        <Form className="form-grid two-columns" onSubmit={() => void save()}>
          <Select
            label="مكان الظهور"
            value={form.placement}
            onChange={(v) => setForm((f) => ({ ...f, placement: v }))}
            options={[
              { value: 'units', label: 'صفحة الوحدات' },
              { value: 'games', label: 'صفحة الألعاب' },
              { value: 'all', label: 'الصفحتان' },
            ]}
          />
          <Input
            label="العنوان (داخلي)"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />
          <Input
            label="الترتيب"
            type="number"
            value={String(form.sort_order)}
            onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))}
          />
          <VisibilitySelect
            value={form.is_active}
            onChange={(v) => setForm((f) => ({ ...f, is_active: Number(v) }))}
          />
          <ImagePicker
            value={form.image_url}
            onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
            onNotify={(m) => setMsg(m)}
          />
          <FormActions>
            <Button type="submit" disabled={busy}>
              {busy ? 'جاري الحفظ...' : 'حفظ السلايد'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setForm(emptySlide)}>
              تفريغ
            </Button>
          </FormActions>
        </Form>
      </SectionCard>

      <SectionCard title="المعاينة والتصفية" icon={<Icon name="settings" size={24} />}>
        <TabsRow
          tabs={[
            { key: 'all', label: 'الكل' },
            { key: 'units', label: 'وحدات' },
            { key: 'games', label: 'ألعاب' },
          ]}
          value={filter}
          onChange={setFilter}
        />

        <div className="slide-preview-list top-space">
          {filtered.length === 0 ? (
            <Empty title="لا توجد سلايدات" hint="أضف أول صورة للسلايدر من النموذج." />
          ) : (
            filtered.map((s) => {
              const img = mediaUrl(s.image_url);
              return (
                <article key={str(s.id)} className="slide-admin-card">
                  <div className="slide-admin-img">{img ? <img src={img} alt="" /> : null}</div>
                  <div>
                    <strong>{placementLabel(s.placement)}</strong>
                    <small className="muted block">
                      {Number(s.is_active) === 0 ? 'مخفي' : 'ظاهر'} — ترتيب {str(s.sort_order)}
                    </small>
                  </div>
                  <div className="table-actions">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setForm({ ...emptySlide, ...s, id: Number(s.id) })}
                    >
                      تعديل
                    </Button>
                    <Button type="button" variant="danger" onClick={() => void remove(s.id)}>
                      حذف
                    </Button>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </SectionCard>
    </ScreenFrame>
  );
}
